import React, { useEffect, useMemo, useState } from 'react';
import { Formik, Form as FormikForm } from 'formik';
import * as Yup from 'yup';
import { Button, Card, Col, Container, Form, Modal, Row, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { FaExchangeAlt, FaPlus, FaTrash } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import Tabela from '~/Components/TabelaDinamica/Tabela';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { GesRetencoesService } from '~/Services/Service';

const validationSchema = Yup.object().shape({
  codigo_usuario: Yup.number()
    .required('Usuário deve ser informado.')
    .positive('Usuário deve ser informado.')
    .integer('Usuário deve ser informado.'),
  nivel: Yup.number()
    .typeError('Nível deve ser numérico.')
    .required('Nível deve ser informado.')
    .integer('Nível deve ser inteiro.')
    .min(1, 'Nível deve ser maior que zero.'),
});

const extrairMensagemErro = (err, fallback) => {
  if (err && err.message) return err.message;
  if (typeof err === 'string') return err;
  return fallback;
};

const nomeUsuario = (usuario) => {
  if (!usuario) return '';
  if (usuario.nome) return usuario.nome;
  if (usuario.nome_completo) return usuario.nome_completo;
  return usuario.nome_usuario || '';
};

const codigoUsuario = (usuario) => usuario && usuario.codigo_usuario;

const statusLabel = (status) => {
  if (status === 'A') return 'Ativo';
  if (status === 'I') return 'Inativo';
  return status || '-';
};

const statusVariant = (status) => (status === 'A' ? 'success' : 'secondary');

const ordenarRetencoes = (lista) =>
  [...lista].sort((a, b) => {
    const nivelA = a.nivel != null ? a.nivel : 0;
    const nivelB = b.nivel != null ? b.nivel : 0;
    if (nivelA !== nivelB) return nivelA - nivelB;
    return nomeUsuario(a).localeCompare(nomeUsuario(b));
  });

const NivelRetencao = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [retencoes, setRetencoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalAberto, setModalAberto] = useState(false);
  const [confirmacao, setConfirmacao] = useState({
    show: false,
    tipo: '',
    item: null,
  });

  const carregarDados = () => {
    setLoading(true);
    Promise.all([
      GesRetencoesService.listarUsuariosAtivos(),
      GesRetencoesService.listarUsuariosRetencao(),
    ])
      .then(([usuariosResp, retencoesResp]) => {
        setUsuarios(Array.isArray(usuariosResp) ? usuariosResp : []);
        setRetencoes(ordenarRetencoes(Array.isArray(retencoesResp) ? retencoesResp : []));
      })
      .catch((err) => {
        const msg = extrairMensagemErro(err, 'Erro ao carregar níveis de retenção.');
        toast.error(msg);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let ativo = true;
    setLoading(true);
    Promise.all([
      GesRetencoesService.listarUsuariosAtivos(),
      GesRetencoesService.listarUsuariosRetencao(),
    ])
      .then(([usuariosResp, retencoesResp]) => {
        if (!ativo) return;
        setUsuarios(Array.isArray(usuariosResp) ? usuariosResp : []);
        setRetencoes(ordenarRetencoes(Array.isArray(retencoesResp) ? retencoesResp : []));
      })
      .catch((err) => {
        if (!ativo) return;
        const msg = extrairMensagemErro(err, 'Erro ao carregar níveis de retenção.');
        toast.error(msg);
      })
      .finally(() => {
        if (ativo) setLoading(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  const opcoesUsuarios = useMemo(() => {
    const usuariosComRetencao = new Set(
      retencoes.map((item) => codigoUsuario(item)).filter((codigo) => codigo != null)
    );
    return usuarios
      .filter((usuario) => !usuariosComRetencao.has(codigoUsuario(usuario)))
      .map((usuario) => ({
        value: codigoUsuario(usuario),
        label: `${codigoUsuario(usuario)} - ${nomeUsuario(usuario)}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [usuarios, retencoes]);

  const dadosTabela = useMemo(
    () =>
      retencoes.map((item) => ({
        ...item,
        id: `${item.codigo_usuario}`,
        usuario_display: `${item.codigo_usuario} - ${nomeUsuario(item)}`,
        status_display: statusLabel(item.status),
      })),
    [retencoes]
  );

  const handleSalvar = (values, { setSubmitting, resetForm }) => {
    const payload = {
      codigo_usuario: values.codigo_usuario,
      nivel: Number(values.nivel),
    };
    GesRetencoesService.incluirUsuarioRetencao(payload)
      .then(() => {
        toast.success('Nível de retenção cadastrado.');
        resetForm();
        setModalAberto(false);
        carregarDados();
      })
      .catch((err) => {
        const msg = extrairMensagemErro(err, 'Erro ao cadastrar nível de retenção.');
        toast.error(msg);
      })
      .finally(() => setSubmitting(false));
  };

  const solicitarStatus = (item) => {
    setConfirmacao({ show: true, tipo: 'status', item });
  };

  const solicitarExclusao = (item) => {
    setConfirmacao({ show: true, tipo: 'excluir', item });
  };

  const handleConfirmacao = (confirmado) => {
    const { tipo, item } = confirmacao;
    setConfirmacao({ show: false, tipo: '', item: null });
    if (!confirmado || !item) return;

    const acao =
      tipo === 'status'
        ? GesRetencoesService.alterarStatusUsuarioRetencao({
            codigo_usuario: item.codigo_usuario,
          })
        : GesRetencoesService.excluirUsuarioRetencao({
            codigo_usuario: item.codigo_usuario,
          });

    acao
      .then(() => {
        toast.success(
          tipo === 'status'
            ? 'Status do nível de retenção atualizado.'
            : 'Nível de retenção excluído.'
        );
        carregarDados();
      })
      .catch((err) => {
        const msg = extrairMensagemErro(
          err,
          tipo === 'status'
            ? 'Erro ao alterar status do nível de retenção.'
            : 'Erro ao excluir nível de retenção.'
        );
        toast.error(msg);
      });
  };

  const colunas = [
    { title: 'Usuário', key: 'usuario_display', ordenacao: true },
    { title: 'Nível', key: 'nivel', ordenacao: true, width: '120px', align: 'center' },
    {
      title: 'Status',
      key: 'status_display',
      ordenacao: true,
      width: '140px',
      align: 'center',
      render: (value, item) => (
        <span className={`badge badge-${statusVariant(item.status)}`}>{value}</span>
      ),
    },
  ];

  const acoes = [
    {
      label: '',
      title: 'Ativar/Inativar',
      icon: <FaExchangeAlt />,
      type: 'success',
      onClick: solicitarStatus,
    },
    {
      label: '',
      title: 'Excluir',
      icon: <FaTrash />,
      type: 'danger',
      onClick: solicitarExclusao,
    },
  ];

  const confirmMessage = confirmacao.item
    ? confirmacao.tipo === 'status'
      ? `Deseja ${confirmacao.item.status === 'I' ? 'ativar' : 'inativar'} o nível de retenção do usuário ${nomeUsuario(confirmacao.item)}?`
      : `Deseja excluir o nível de retenção do usuário ${nomeUsuario(confirmacao.item)}?`
    : '';

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Nível de Retenção" />

      <Card className="shadow-sm mb-3">
        <Card.Header
          className="d-flex justify-content-between align-items-center"
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
        >
          <strong>Níveis cadastrados ({dadosTabela.length})</strong>
          <Button variant="light" size="sm" onClick={() => setModalAberto(true)}>
            <FaPlus className="mr-1" /> Novo nível
          </Button>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <Tabela
              colunas={colunas}
              dados={dadosTabela}
              action
              acoes={acoes}
              actionColumnWidth="120px"
            />
          )}
        </Card.Body>
      </Card>

      <Modal
        show={modalAberto}
        onHide={() => setModalAberto(false)}
        centered
        backdrop="static"
      >
        <Formik
          initialValues={{ codigo_usuario: '', nivel: '' }}
          validationSchema={validationSchema}
          onSubmit={handleSalvar}
        >
          {({ errors, isSubmitting, setFieldValue, touched, values }) => (
            <FormikForm noValidate>
              <Modal.Header closeButton style={{ backgroundColor: '#0747a6', color: '#fff' }}>
                <Modal.Title style={{ fontSize: '1rem', color: '#fff' }}>
                  Novo nível de retenção
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Row>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Usuário</Form.Label>
                      <Select
                        inputId="codigo_usuario"
                        options={opcoesUsuarios}
                        placeholder="Selecione"
                        value={
                          opcoesUsuarios.find((opcao) => opcao.value === values.codigo_usuario) ||
                          null
                        }
                        onChange={(opcao) =>
                          setFieldValue('codigo_usuario', opcao ? opcao.value : '')
                        }
                        isClearable
                        components={{ DropdownIndicator }}
                        styles={customStyles}
                        noOptionsMessage={() => 'Nenhum usuário disponível'}
                      />
                      {touched.codigo_usuario && errors.codigo_usuario && (
                        <div className="text-danger small mt-1">{errors.codigo_usuario}</div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Nível</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        name="nivel"
                        value={values.nivel}
                        onChange={(e) => setFieldValue('nivel', e.target.value)}
                        isInvalid={touched.nivel && !!errors.nivel}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.nivel}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setModalAberto(false)}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button type="submit" variant="primary" disabled={isSubmitting}>
                  Salvar
                </Button>
              </Modal.Footer>
            </FormikForm>
          )}
        </Formik>
      </Modal>

      <ModalConfirmacao
        show={confirmacao.show}
        title={confirmacao.tipo === 'status' ? 'Confirmar status' : 'Confirmar exclusão'}
        message={confirmMessage}
        variant={confirmacao.tipo === 'status' ? 'success' : 'danger'}
        onClose={handleConfirmacao}
      />
    </Container>
  );
};

export default NivelRetencao;
